
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { SEO } from '../layout/SEO';

interface BlogPostTemplateProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  date: string;
  readTime?: string;
  path: string;
  keywords?: string[];
  children: React.ReactNode;
}

export function BlogPostTemplate({
  title,
  metaTitle,
  metaDescription,
  category,
  date,
  readTime = "5 min read",
  path,
  keywords = [],
  children
}: BlogPostTemplateProps) {
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `Check out this article from XXL IT Services: ${title}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback to copying URL to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        // Could add toast notification here
      } catch (err) {
        console.log('Error copying to clipboard:', err);
      }
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'cybersecurity':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'it support':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'network infrastructure':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'connectivity':
        return 'bg-purple-50 text-purple-600 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
    <>
      <SEO 
        title={metaTitle}
        description={metaDescription}
        path={path}
        keywords={keywords}
        type="article"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black">
        <div className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-0">
            <div className="p-6">
              {/* Back to Blog */}
              <Link
                to="/blog"
                className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 mb-8 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Link>

              {/* Featured Image Placeholder */}
              <div className="relative h-64 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg mb-8 overflow-hidden">
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute bottom-4 left-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(category)}`}>
                    {category}
                  </span>
                </div>
              </div>

              {/* Article Header */}
              <header className="mb-10">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight" id="article-title" role="banner">
                  {title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-300 mb-6">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{date}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{readTime}</span>
                  </div>
                  <button
                    onClick={handleShare}
                    className="flex items-center hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    <span>Share</span>
                  </button>
                </div>
              </header>

              {/* Article Content */}
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <div className="prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-ol:text-gray-700 dark:prose-ol:text-gray-300">
                  {children}
                </div>
              </div>

              <hr className="my-10 border-gray-300 dark:border-gray-600" />

              {/* Author Bio */}
              <div className="mt-10 p-6 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold">
                    XXL
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      XXL IT Services Team
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Our certified IT team has served Treasure Valley businesses since 2005. We provide expert 
                      technology solutions throughout Meridian, Boise, Nampa, and surrounding areas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link 
                        to="/contact"
                        className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                      >
                        Get IT Consultation
                      </Link>
                      <a 
                        href="tel:+12083284500"
                        className="inline-flex items-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Call (208) 328-4500
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Services CTA */}
              <div className="mt-10 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                  Need Professional IT Services?
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  From network security to 24/7 support, XXL IT Services provides comprehensive 
                  technology solutions for Treasure Valley businesses.
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <Link 
                    to="/services/it-support"
                    className="block p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      IT Support
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      24/7 monitoring and technical support
                    </p>
                  </Link>
                  <Link 
                    to="/services/cybersecurity"
                    className="block p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Cybersecurity
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Advanced threat protection and security
                    </p>
                  </Link>
                  <Link 
                    to="/services/network"
                    className="block p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Network Solutions
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Professional network infrastructure
                    </p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Reusable callout components for blog content
export function TipCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 text-blue-900 dark:text-blue-100 p-4 text-sm rounded my-6">
      <div className="flex items-start">
        <span className="text-lg mr-2">💡</span>
        <div>{children}</div>
      </div>
    </div>
  );
}

export function WarningCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 text-yellow-900 dark:text-yellow-100 p-4 text-sm rounded my-6">
      <div className="flex items-start">
        <span className="text-lg mr-2">⚠️</span>
        <div>{children}</div>
      </div>
    </div>
  );
}

export function SuccessCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 text-green-900 dark:text-green-100 p-4 text-sm rounded my-6">
      <div className="flex items-start">
        <span className="text-lg mr-2">✅</span>
        <div>{children}</div>
      </div>
    </div>
  );
}

export function ImportantCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-900 dark:text-red-100 p-4 text-sm rounded my-6">
      <div className="flex items-start">
        <span className="text-lg mr-2">🚨</span>
        <div>{children}</div>
      </div>
    </div>
  );
}